import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useMembersStore } from '@/store/membersStore';
import type { AppRole } from '@/backend/types';
import { Button, Chip, EmptyState, Screen, SectionLabel, TextField } from '../../ui';

// The two the phone offers. 'owner' cannot be invited and 'blocked' is not a grant.
const ROLES: AppRole[] = ['member', 'editor'];

/** Ported from src/app/(main)/members/[appId].tsx. */
export default function Members() {
  const { appId = '' } = useParams();
  const members = useMembersStore((s) => s.members);
  const error = useMembersStore((s) => s.error);
  const lastInvite = useMembersStore((s) => s.lastInvite);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<AppRole>('member');
  const [busy, setBusy] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState<string | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    void useMembersStore.getState().load(appId);
  }, [appId]);

  async function invite(): Promise<void> {
    if (!email.trim()) return;
    setBusy(true);
    try {
      await useMembersStore.getState().invite(appId, { email: email.trim(), role });
      setEmail('');
    } finally {
      setBusy(false);
    }
  }

  async function confirmRemove(): Promise<void> {
    const userId = pendingRemoval;
    dialog.current?.close();
    setPendingRemoval(null);
    if (userId) await useMembersStore.getState().remove(appId, userId);
  }

  return (
    <Screen title="Members">
      {error && <p role="alert" className="type-caption field-error-text">{error}</p>}

      <SectionLabel>Invite someone</SectionLabel>
      <TextField label="Email" type="email" value={email} onChangeText={setEmail} />
      <label className="type-caption field-label" htmlFor="role">Role</label>
      <select id="role" className="field" value={role} onChange={(e) => setRole(e.target.value as AppRole)}>
        {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
      </select>
      <Button title="Send invite" onPress={() => void invite()} loading={busy} disabled={!email.trim()} />

      {lastInvite?.token && (
        <p role="status" className="type-body publish-url">
          Invite link: <code>{`${window.location.origin}/invite/${lastInvite.token}`}</code>
        </p>
      )}

      <SectionLabel>Who has access</SectionLabel>
      {members.length === 0 ? (
        <EmptyState icon="account" title="No members yet" body="Invite someone above to share this app." />
      ) : (
        <ul className="project-list">
          {members.map((m) => (
            <li key={m.userId} className="card clay-card project-row">
              <span className="project-text">
                <span className="type-heading">{m.email ?? m.userId}</span>
                <span className="type-caption project-meta">Joined {m.joinedAt?.slice(0, 10)}</span>
              </span>
              <Chip label={m.role} tone={m.role === 'owner' ? 'primary' : 'neutral'} />
              {m.role !== 'owner' && (
                <Button
                  title="Remove"
                  variant="ghost"
                  onPress={() => {
                    setPendingRemoval(m.userId);
                    dialog.current?.showModal();
                  }}
                />
              )}
            </li>
          ))}
        </ul>
      )}

      <dialog ref={dialog} aria-label="Remove this member" className="confirm-dialog">
        <h2 className="type-title">Remove this member?</h2>
        <p className="type-body">They lose access to the app immediately.</p>
        <div className="build-actions">
          <Button title="Yes, remove" variant="dangerSoft" onPress={() => void confirmRemove()} />
          <Button title="Cancel" variant="ghost" onPress={() => { dialog.current?.close(); setPendingRemoval(null); }} />
        </div>
      </dialog>
    </Screen>
  );
}
